import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Link2, Loader2, Check, X, Trash2, Send, Clock, ShieldCheck } from "lucide-react";

const COPY = {
  vet: {
    title: "Linked farm accounts",
    subtitle:
      "Connect to a farm's own EquiMare account so you both share the same live records. No account yet? Invite them anyway — they'll get an email and connect once they sign up. Linked farms appear in your farm switcher.",
    placeholder: "Farm account email",
    inviteLabel: "Invite farm",
    emptyLabel: "No linked farm accounts yet.",
  },
  farm: {
    title: "Veterinarian access",
    subtitle:
      "Invite your veterinarian to share your live records, or accept an invitation from them. They can view and edit your mares, foals and rounds. If they don't have an account yet, invite them anyway — they'll connect once they sign up.",
    placeholder: "Veterinarian's email",
    inviteLabel: "Invite vet",
    emptyLabel: "No veterinarians linked yet.",
  },
};

export default function AccountLinks({ role, onChange }) {
  const copy = COPY[role];
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/links");
      setLinks(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load links");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async () => {
    if (!email.trim()) {
      toast.error("Enter an email address");
      return;
    }
    setInviting(true);
    try {
      const { data } = await api.post("/links/invite", { email: email.trim() });
      toast.success(
        data?.pending_signup
          ? "Invitation sent — they'll connect once they create an account with that email"
          : "Invitation sent"
      );
      setEmail("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send invite");
    } finally {
      setInviting(false);
    }
  };

  const respond = async (link, action) => {
    setBusyId(link.id);
    try {
      await api.post(`/links/${link.id}/respond`, { action });
      toast.success(action === "accept" ? "Linked — records are now shared" : "Invitation declined");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (link) => {
    const verb = link.status === "accepted" ? "unlink" : "cancel";
    if (!window.confirm(`Are you sure you want to ${verb} ${link.counterparty_label}?`)) return;
    setBusyId(link.id);
    try {
      await api.delete(`/links/${link.id}`);
      toast.success(link.status === "accepted" ? "Unlinked" : "Invitation cancelled");
      await load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-5" data-testid="account-links">
      <div className="flex items-center gap-2">
        <Link2 className="h-5 w-5 text-primary" />
        <h2 className="font-bold text-lg">{copy.title}</h2>
      </div>
      <p className="text-sm text-muted-foreground -mt-2">{copy.subtitle}</p>

      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && invite()}
          placeholder={copy.placeholder}
          data-testid="link-invite-email"
        />
        <Button onClick={invite} disabled={inviting} className="gap-2 shrink-0" data-testid="link-invite-btn">
          {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {copy.inviteLabel}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : links.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4" data-testid="links-empty">{copy.emptyLabel}</p>
      ) : (
        <div className="space-y-2.5">
          {links.map((l) => (
            <div
              key={l.id}
              data-testid={`link-row-${l.id}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/30 px-4 py-3"
            >
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{l.counterparty_label}</p>
                <p className="text-xs text-muted-foreground truncate">{l.counterparty_email}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {l.status === "accepted" ? (
                  <>
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-emerald-700 bg-emerald-100 rounded-full px-2.5 py-1">
                      <ShieldCheck className="h-3.5 w-3.5" /> Linked
                    </span>
                    <Button variant="outline" size="icon" className="h-8 w-8 text-destructive" disabled={busyId === l.id} onClick={() => remove(l)} data-testid={`link-unlink-${l.id}`}>
                      {busyId === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </>
                ) : l.actionable ? (
                  <>
                    <span className="text-xs text-muted-foreground hidden sm:inline">Invitation received</span>
                    <Button size="sm" className="gap-1.5 h-8" disabled={busyId === l.id} onClick={() => respond(l, "accept")} data-testid={`link-accept-${l.id}`}>
                      {busyId === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Accept
                    </Button>
                    <Button variant="outline" size="sm" className="gap-1.5 h-8" disabled={busyId === l.id} onClick={() => respond(l, "decline")} data-testid={`link-decline-${l.id}`}>
                      <X className="h-4 w-4" /> Decline
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 rounded-full px-2.5 py-1">
                      <Clock className="h-3.5 w-3.5" /> Pending
                    </span>
                    <Button variant="outline" size="icon" className="h-8 w-8 text-destructive" disabled={busyId === l.id} onClick={() => remove(l)} data-testid={`link-cancel-${l.id}`}>
                      {busyId === l.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
